import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ArrowLeft, MailCheck, AlertTriangle } from "lucide-react";
import { requestLink, devAvailable, devLogin } from "@/lib/api";
import { useAuth, formatApiError } from "@/lib/auth";
import { landing } from "@/lib/access";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandLogo } from "@/components/brand/AppHeader";

const LINK_ERRORS = {
  expired: "That sign-in link has expired. Links work for 15 minutes — request a new one below.",
  used: "That sign-in link was already used. Each link works once — request a new one below.",
  invalid: "That sign-in link isn't valid. Request a new one below.",
  stepup: "That confirmation link couldn't be used in this browser. Please try again.",
  inactive: "This account can't sign in right now. Please contact us for help.",
  invite_expired: "Your invitation link has expired. Tap the button below and we'll email you a fresh sign-in link.",
  invite_used: "Your invitation link was already used. Tap the button below and we'll email you a fresh sign-in link.",
  invite_invalid: "That invitation link isn't valid. Request a sign-in link below.",
  invite_revoked: "This invitation is no longer active. Please contact the horse's owner or our team.",
};
const safeNext = (n) => (n && n.startsWith("/") && !n.startsWith("//") && !n.includes("\\") && !n.startsWith("/login") ? n : null);

function useCountdown() {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (left <= 0) return undefined;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  return [left, setLeft];
}

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const linkError = LINK_ERRORS[params.get("error")];
  const { user, setUser } = useAuth();
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState(params.get("email") || "");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [devOk, setDevOk] = useState(false);
  const [cooldown, setCooldown] = useCountdown();

  useEffect(() => {
    if (user && user.role) navigate(next || landing(user), { replace: true });
  }, [user, navigate, next]);
  useEffect(() => { devAvailable().then((d) => setDevOk(!!d.dev_login)).catch(() => setDevOk(false)); }, []);

  const enterDev = async () => {
    setBusy(true);
    try {
      const u = await devLogin();
      setUser(u);
      toast.success("Signed in (developer mode)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return toast.error("Enter your email");
    setBusy(true);
    try {
      const r = await requestLink(email.trim().toLowerCase(), name.trim() || undefined, next || undefined);
      if (r.sandbox_skipped) { toast.warning(r.message); setCooldown(r.cooldown || 60); return; }
      setSent(true);
      setCooldown(r.cooldown || 60);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      if (e2.response?.status === 429 && d?.retry_after) setCooldown(d.retry_after);
      toast.error(formatApiError(d));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-7">
          <BrandLogo light className="mx-auto mb-4 w-64 sm:w-80 max-w-full h-auto" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">Seller Portal</h1>
          <p className="text-sm text-stone-500 mt-2">{sent ? "One more step." : "Sign in or create your account with just your email. No password needed."}</p>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl shadow-sm p-6 sm:p-7">
          {linkError && !sent && (
            <div data-testid="login-link-error" className="mb-4 flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>{linkError}</span>
            </div>
          )}
          {sent ? (
            <div data-testid="login-check-email" className="space-y-4 text-center">
              <MailCheck className="h-12 w-12 mx-auto text-amber-800" />
              <h2 className="text-lg font-bold text-stone-900">Check your email</h2>
              <p className="text-sm text-stone-600">If <strong>{email}</strong> can sign in, we've sent a sign-in link. Click it on this device — it works once and expires in 15 minutes.</p>
              <p data-testid="login-spam-reminder" className="text-xs text-stone-500">Didn't receive the email? Check your spam or junk folder. Sometimes our sign-in emails end up there.</p>
              <Button data-testid="login-resend-button" onClick={send} disabled={busy || cooldown > 0} variant="outline" className="w-full rounded-full">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                {cooldown > 0 ? `Resend link in ${cooldown}s` : "Resend link"}
              </Button>
              <button type="button" data-testid="login-back-button" onClick={() => setSent(false)} className="text-xs text-stone-500 hover:text-stone-800 inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={send} className="space-y-4" data-testid="login-email-form">
              <div>
                <label htmlFor="login-email" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Email</label>
                <Input id="login-email" data-testid="login-email-input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" autoFocus className="border-stone-200 focus-visible:ring-amber-800 h-11" />
              </div>
              <div>
                <label htmlFor="login-name" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Name <span className="text-stone-500">(new accounts)</span></label>
                <Input id="login-name" data-testid="login-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                  className="border-stone-200 focus-visible:ring-amber-800 h-11" />
              </div>
              <Button data-testid="login-send-button" type="submit" disabled={busy || cooldown > 0} className="w-full h-11 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                {cooldown > 0 ? `Try again in ${cooldown}s` : "Email Me a Sign-In Link"}
              </Button>
              {devOk && (
                <button type="button" data-testid="dev-login-button" onClick={enterDev} disabled={busy} className="w-full text-center text-xs text-amber-800 hover:underline pt-1">
                  Developer sign-in (admin, preview only — no email)
                </button>
              )}
            </form>
          )}
        </div>
        <p className="text-center text-[11px] text-stone-400 mt-4">You'll stay signed in on this device until you log out or after 7 days without activity.</p>
      </div>
    </div>
  );
}
